import { useLayoutEffect, useRef } from "react";
import { T } from "../../../app/tokens.js";
import { serializeWeeklyDom, weeklyHtml } from "../../../lib/weeklyRichText.js";
import { btn, inputStyle } from "./weeklyUi.js";

const COMMANDS = [["bold", "Bold", "B"], ["italic", "Italic", "I"], ["insertUnorderedList", "Bulleted list", "• ≡"], ["insertOrderedList", "Numbered list", "1. ≡"]];

export function WeeklyRichText({ value, ...props }) {
  return <div {...props} dangerouslySetInnerHTML={{ __html: weeklyHtml(value) }} />;
}

export function WeeklyRichEditor({ value, onChange, disabled, "aria-label": label, "data-testid": testId, style }) {
  const ref = useRef(null);
  const last = useRef(null);
  useLayoutEffect(() => {
    if (last.current !== value && ref.current) ref.current.innerHTML = weeklyHtml(value);
    last.current = value;
  }, [value]);
  const emit = () => {
    const next = serializeWeeklyDom(ref.current);
    last.current = next;
    onChange(next);
  };
  const command = name => {
    if (disabled) return;
    ref.current.focus();
    document.execCommand(name, false);
    emit();
  };
  return <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden", ...style }}>
    <div role="toolbar" aria-label={`${label} formatting`} style={{ display: "flex", gap: 4, padding: 5, borderBottom: `1px solid ${T.border}` }}>
      {COMMANDS.map(([name, title, text]) => <button key={name} type="button" aria-label={title} title={title} disabled={disabled}
        onMouseDown={e => e.preventDefault()} onClick={() => command(name)}
        style={{ ...btn, padding: "3px 8px", fontWeight: T.fwBold, fontStyle: name === "italic" ? "italic" : undefined }}>{text}</button>)}
    </div>
    <div ref={ref} role="textbox" aria-multiline="true" aria-label={label} data-testid={testId} contentEditable={!disabled} suppressContentEditableWarning spellCheck
      onInput={emit} onKeyDown={e => {
        if ((e.ctrlKey || e.metaKey) && ["b", "i"].includes(e.key.toLowerCase())) { e.preventDefault(); command(e.key.toLowerCase() === "b" ? "bold" : "italic"); }
      }}
      onPaste={e => {
        e.preventDefault();
        if (disabled) return;
        // Template contents are inert: pasted URLs must not load before
        // rebuilding the permitted text/emphasis/list subset.
        const container = document.createElement("template");
        const html = e.clipboardData.getData("text/html");
        if (html) container.innerHTML = html;
        else container.content.append(document.createTextNode(e.clipboardData.getData("text/plain")));
        const safe = weeklyHtml(serializeWeeklyDom(container.content));
        document.execCommand("insertHTML", false, safe);
        emit();
      }}
      onDrop={e => e.preventDefault()}
      style={{ ...inputStyle, border: 0, borderRadius: 0, minHeight: 90, whiteSpace: "pre-wrap", textTransform: "none", letterSpacing: "normal", fontWeight: T.fwNormal, padding: 10 }} />
  </div>;
}
